<template>
  <v-card
    id="pwt-info-panel"
    :width="mobile ? 'initial' : '40%'"
    height="100%"
    :class="classes"
    :min-width="mobile ? `100%` : 400"
    :max-width="mobile ? `100%` : 600"
    color="pwt_snow"
  >
    <!-- Account details card -->

    <div
      class="rounded-0 pa-0 bg-pwt_snow"
      style="border-bottom: 2px solid #dadada"
      :style="showBreadcrumbs ? 'height: 150px' : 'height: 100px'"
    >
      <v-card
        elevation="0"
        class="d-flex justify-space-between align-center pl-5 mb-1 rounded-0"
        height="90"
        @click="emitShowDashboard"
        @keydown="keyDown($event)"
        color="transparent"
      >
        <div class="d-flex align-center justify-center">
          <v-avatar color="pwt_gainsboro">
            <img v-if="avatarSrc" :src="avatarSrc" width="100%" height="auto" />
            <v-icon v-else color="pwt_light_grey"> mdi-account-circle </v-icon>
          </v-avatar>
          <v-card flat color="transparent">
            <v-card-title
              class="pb-0 pt-0 text-pwt_black"
              style="font-size: 20px; line-height: 1.2"
            >
              {{ usersName ?? 'Guest user' }}
            </v-card-title>
            <v-card-subtitle
              v-if="!authenticated"
              class="text-pwt_black"
              style="font-size: 12px; line-height: 1.2; opacity: 1"
            >
              Please <LoginLink /> to see your details.
            </v-card-subtitle>
            <v-card-subtitle
              v-else
              class="text-pwt_black"
              style="font-size: 12px; line-height: 1.2"
            >
              {{ emailAddress ?? '' }}
            </v-card-subtitle>
          </v-card>
        </div>
        <v-btn
          color="pwt_off_white"
          flat
          class="align-self-start ma-2 mt-5"
          style="border-radius: 8px"
          @click.stop="emitMinimize"
        >
          <v-icon size="25" color="pwt_medium_grey" >mdi-window-minimize </v-icon>
        </v-btn>
      </v-card>
      <TripNavBar v-if="showBreadcrumbs" />
    </div>
    <v-card
      flat
      class="pwt-scroll-container overflow-y-auto pwt-scrollbar-styles"
      style="padding-bottom: 100px"
      id="info-panel-scroll-container"
      color="pwt_snow"
    >
      <router-view />
    </v-card>
  </v-card>
</template>

<script setup lang="ts">
import Driver from '@/classes/user_classes/driver'
import { GettersTypes, type State } from '@/store/store_types'
import getAssetSrc from '@/utils/getAssetSrc'
import { computed, nextTick, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useDisplay } from 'vuetify'
import { useStore } from 'vuex'
import LoginLink from '../auth/LoginLink.vue'
import TripNavBar from '../ui-elements/nav/TripNavBar.vue'

const { animation } = defineProps<{ animation: 'slide-left' | 'slide-right' | null }>()

const store = useStore<State>()

const avatarSrc = computed(() => {
  const driver: Driver | undefined = store.getters[GettersTypes.currentDriver]
  return driver?.profilePicSrc ?? getAssetSrc('No account icon.svg')
})

const { mobile } = useDisplay()

const usersName = computed(() => store.state.user?.fullName)
const emailAddress = computed(() => store.state.user?.email)
const authenticated = computed(() => store.state.authorizedFlag)

const classes = computed(() => {
  if (animation == 'slide-left') {
    return mobile.value
      ? 'overflow-hidden ma-0 rounded-0'
      : 'overflow-hidden ma-0 rounded-0 pwt-info-panel-right-to-left-animation'
  }

  if (animation == 'slide-right') {
    return mobile.value
      ? 'overflow-hidden ma-0 rounded-0'
      : 'overflow-hidden ma-0 rounded-0 pwt-info-panel-left-to-right-animation'
  }

  return mobile.value ? 'overflow-hidden ma-0 rounded-0' : 'overflow-hidden ma-0 rounded-0'
})

const emit = defineEmits(['minimize', 'showDashboard'])

const emitMinimize = () => {
  emit('minimize')
}

const emitShowDashboard = () => {
  emit('showDashboard')
}


const keyDown = (event: any) => {
  if (event.key === 'Enter') emitShowDashboard()
}

const resetScrollPosition = () => {
  nextTick(() => {
    const myDiv = document.getElementById('info-panel-scroll-container')
    if (myDiv) myDiv.scrollTop = 0
  })
}

const route = useRoute()

watch(route, (to, from) => {
  if (to.name !== from.name) {
    resetScrollPosition()
  }
})

const showBreadcrumbs = computed(() => {
  if (route.name !== 'trips' && route.name !== 'tripAddDestination') return true
  return false
})
</script>

<style scoped>
#pwt-info-panel {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1102;
  border-radius: 10px;
}

#pwt-info-panel-expand-btn {
  z-index: 1102;
  position: absolute;
  bottom: 8px;
  left: 8px;
}

#pwt-info-panel-expand-btn-mobile {
  z-index: 1102;
  position: absolute;
  top: 5px;
  left: 5px;
}

#pwt-info-panel-minimize-btn {
  z-index: 1102;
  position: absolute;
  top: 0;
  left: clamp(410px, calc(40% + 10px), 610px);
}

#pwt-info-panel-floating-controls-overlay {
  z-index: 1102;
  position: relative;
  min-width: 400px;
  max-width: 600px;
  width: 40%;
  height: calc(100% - 30px) !important;
  background-color: transparent;
}

.pwt-info-panel-left-to-right-animation {
  left: -41%;
  -webkit-animation: left-to-right 700ms linear forwards;
  animation: left-to-right 700ms linear forwards;
}

.pwt-info-panel-right-to-left-animation {
  left: 0;
  -webkit-animation: right-to-left 1s linear forwards;
  animation: right-to-left 1s linear forwards;
}

@-webkit-keyframes left-to-right {
  from {
    left: -41%;
  }
  to {
    left: 0;
  }
}
@-webkit-keyframes right-to-left {
  from {
    left: 0;
  }
  to {
    left: -100%;
  }
}

@keyframes left-to-right {
  from {
    left: -41%;
  }
  to {
    left: 0;
  }
}
@keyframes right-to-left {
  from {
    left: 0;
  }
  to {
    left: -100%;
  }
}

.pwt-scroll-container {
  width: 100%;
  height: 100%;
}

.pwt-scrollbar-styles {
  scrollbar-color: #ffffff #e0e0e0;
  scrollbar-width: thin;
}

.pwt-scrollbar-styles:hover {
  scrollbar-color: #eeeeee #e0e0e0;
}

.pwt-scrollbar-styles::-webkit-scrollbar {
  width: 6px;
}

.pwt-scrollbar-styles::-webkit-scrollbar-track {
  background: #ffffff;
}

.pwt-scrollbar-styles::-webkit-scrollbar-track:hover {
  background: #e0e0e0;
}

.pwt-scrollbar-styles::-webkit-scrollbar-thumb {
  background: #ffffff;
}

.pwt-scrollbar-styles::-webkit-scrollbar-thumb:hover {
  background: #eeeeee;
}
</style>
